import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getFormValues } from 'redux-form/immutable';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { icons } from 'cstar-react-primitives/lib/redux-form/icons/iconConst';
import { Container } from 'cstar-react-primitives/lib/redux-form/layout/Container';
import { TertiaryButton } from 'cstar-react-primitives/lib/redux-form/buttons/TertiaryButton';
import { fromJS } from 'immutable';
import {
    NEW_ITEM_FORM_NAME,
    CURRENCY_FIELD,
    DIGITAL_GREETINGS_FIELD
} from '../item/newItemForm';
import FaceplateImg from '../faceplatePresentation/FaceplateImg';
import messageIntl from './messageIntl';
import DigitalGreetingsModal from './DigitalGreetingsModal';
import { getAmountValue } from './digitalGreetingsUtils';
import Check from './images/Check.svg';
import { logError } from '../utils/errorUtils';

const MODAL_ACTIONS = {
    REMOVE: 'remove',
    RESELECT: 'reselect'
};

const SelectedDigitalGreetings = ({
    intl,
    setIsGreetingSelected,
    formName
}) => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalActionRef = useRef(MODAL_ACTIONS.REMOVE);

    const formValues = useSelector(state => getFormValues(formName)(state));
    const selectedCurrency = formValues ? formValues.get(CURRENCY_FIELD) : '';
    const selectedGreetings = formValues ? formValues.get(DIGITAL_GREETINGS_FIELD) : null;
    if (!selectedGreetings) {
        return null;
    }

    const handleModalOpen = (action) => {
        try {
            modalActionRef.current = action;
            const actionText = action === MODAL_ACTIONS.REMOVE
                ? intl.formatMessage(messageIntl.digitalGreetingsRemoveButtonText)
                : intl.formatMessage(messageIntl.digitalGreetingsChangeGreetingsButtonText);

            const linkClickSegmentData = {
                title: actionText,
                referrer: '',
                url: window.location.href || '',
                destination: actionText === intl.formatMessage(messageIntl.digitalGreetingsRemoveButtonText)
                    ? window.location.href : 'POP-UP'
            };
            dispatch({
                type: 'GREETING_LINK_CLICK',
                meta: {
                    segment: {
                        eventAction: 'Link Click',
                        eventData: {
                            ...linkClickSegmentData
                        }
                    }
                }
            });
            setIsModalOpen(true);
        } catch (error) {
            logError('Failed to open modal:', error);
            setIsModalOpen(true);
        }
    };

    const getButtonConfig = () => ({
        remove: {
            btnText: intl.formatMessage(messageIntl.digitalGreetingsRemoveButtonText),
            cashbotName: 'digital-greetings-selected-remove',
            icon: icons.XICON,
            onClick: () => handleModalOpen(MODAL_ACTIONS.REMOVE)
        },
        change: {
            btnText: intl.formatMessage(messageIntl.digitalGreetingsChangeGreetingsButtonText),
            cashbotName: 'digital-greetings-selected-change',
            icon: icons.KEYBOARD_RIGHT,
            onClick: () => handleModalOpen(MODAL_ACTIONS.RESELECT)
        }
    });

    const buttonConfig = getButtonConfig();
    const commonButtonProps = {
        isSmall: true,
        isLeftAligned: false
    };

    const faceplate = fromJS({
        imageURL: selectedGreetings.get('cardFrontUrl'),
        code: 'selected-digital-greeting',
        altText: 'selected digital greeting card'
    });

    return (
        <div
            className='digital-greetings'
            aria-live="polite"
            role="region"
        >
            <div className='digital-greetings-label'>
                <FormattedMessage {...messageIntl.digitalGreetingsSelectedLabel} values={{ price: getAmountValue(intl, selectedCurrency) }} />
            </div>
            <Container customClassName='digital-greetings-selected'>
                <div className='digital-greetings-selected__preview'>
                    <FaceplateImg
                        faceplate={faceplate}
                        cssClass="digital-greetings-selected__preview-card"
                    />
                    <img
                        src={Check}
                        alt="Gift card icon"
                        className="digital-greetings-selected__preview-icon"
                    />
                </div>
                <div className='digital-greetings-selected__content'>
                    <TertiaryButton {...commonButtonProps} {...buttonConfig.remove} />
                    <TertiaryButton {...commonButtonProps} {...buttonConfig.change} />
                </div>
            </Container>
            {isModalOpen && (
                <DigitalGreetingsModal
                    intl={intl}
                    modalActionRef={modalActionRef.current}
                    setIsModalOpen={setIsModalOpen}
                    setIsGreetingSelected={setIsGreetingSelected}
                    formName={formName}
                />
            )}
        </div>
    );
};

SelectedDigitalGreetings.propTypes = {
    intl: intlShape.isRequired,
    setIsGreetingSelected: PropTypes.func.isRequired,
    formName: PropTypes.string
};

SelectedDigitalGreetings.defaultProps = {
    formName: NEW_ITEM_FORM_NAME
};

export default injectIntl(SelectedDigitalGreetings);
